import React, { useState, useRef, useMemo } from 'react';
import {
  Upload,
  Download,
  FileSpreadsheet,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  ArrowRight,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  parseCSV,
  autoDetectMappings,
  mapRowsToImportFormat,
  validateImport,
  executeImport,
  generateCSVTemplate,
  downloadFile,
  type ImportCompanyRow,
} from '@/services/bulkImport';
import type { ImportFieldMapping, ImportResult } from '@/types';

type Step = 'upload' | 'mapping' | 'preview' | 'importing' | 'complete';

export function BulkImportPage() {
  const [step, setStep] = useState<Step>('upload');
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [csvRows, setCsvRows] = useState<Record<string, string>[]>([]);
  const [mappings, setMappings] = useState<ImportFieldMapping[]>([]);
  const [validationResult, setValidationResult] = useState<{
    validRows: number;
    invalidRows: number;
    errors: { row: number; errors: string[] }[];
    warnings: { row: number; warnings: string[] }[];
    duplicates: { row: number; existingCompanyName: string }[];
  } | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [skipDuplicates, setSkipDuplicates] = useState(true);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Available target fields for mapping
  const targetFields = [
    { value: '', label: '-- Skip this column --' },
    { value: 'name', label: 'Company Name *', required: true },
    { value: 'type', label: 'Business Type' },
    { value: 'address', label: 'Street Address' },
    { value: 'city', label: 'City' },
    { value: 'state', label: 'State' },
    { value: 'zip', label: 'ZIP Code' },
    { value: 'phone', label: 'Phone' },
    { value: 'email', label: 'Email' },
    { value: 'website', label: 'Website' },
    { value: 'notes', label: 'Notes' },
    { value: 'salesRepName', label: 'Sales Rep Name' },
    { value: 'contactFirstName', label: 'Contact First Name' },
    { value: 'contactLastName', label: 'Contact Last Name' },
    { value: 'contactEmail', label: 'Contact Email' },
    { value: 'contactPhone', label: 'Contact Phone' },
    { value: 'contactTitle', label: 'Contact Title' },
  ];

  // Check if name field is mapped
  const hasRequiredMapping = useMemo(() => {
    return mappings.some((m) => m.targetField === 'name');
  }, [mappings]);

  // Handle file upload
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    try {
      const text = await file.text();
      const { headers, rows } = parseCSV(text);

      if (headers.length === 0) {
        toast.error('CSV file appears to be empty');
        return;
      }

      if (rows.length === 0) {
        toast.error('No data rows found in CSV');
        return;
      }

      setCsvHeaders(headers);
      setCsvRows(rows);

      // Auto-detect mappings
      const detectedMappings = autoDetectMappings(headers);
      setMappings(detectedMappings);

      setStep('mapping');
      toast.success(`Loaded ${rows.length} rows from CSV`);
    } catch (error) {
      console.error('Failed to parse CSV:', error);
      toast.error('Failed to parse CSV file');
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Update a single mapping
  const updateMapping = (sourceColumn: string, targetField: string) => {
    setMappings(
      mappings.map((m) =>
        m.sourceColumn === sourceColumn ? { ...m, targetField: targetField as any } : m
      )
    );
  };

  // Proceed to preview/validation
  const handleProceedToPreview = async () => {
    if (!hasRequiredMapping) {
      toast.error('Company Name mapping is required');
      return;
    }

    setIsProcessing(true);
    try {
      const importRows = mapRowsToImportFormat(csvRows, mappings);
      const result = await validateImport(importRows);

      setValidationResult(result);
      setStep('preview');
    } catch (error: any) {
      console.error('Validation error:', error);
      toast.error(error.message || 'Failed to validate data');
    } finally {
      setIsProcessing(false);
    }
  };

  // Execute the import
  const handleExecuteImport = async () => {
    setStep('importing');
    setIsProcessing(true);

    try {
      const importRows = mapRowsToImportFormat(csvRows, mappings);
      const result = await executeImport(importRows, skipDuplicates);

      setImportResult(result);
      setStep('complete');
      toast.success(
        `Imported ${result.importedCompanies} companies and ${result.importedContacts} contacts`
      );
    } catch (error: any) {
      console.error('Import error:', error);
      toast.error(error.message || 'Import failed');
      setStep('preview');
    } finally {
      setIsProcessing(false);
    }
  };

  // Reset and start over
  const handleReset = () => {
    setStep('upload');
    setCsvHeaders([]);
    setCsvRows([]);
    setMappings([]);
    setValidationResult(null);
    setImportResult(null);
  };

  // Download template
  const handleDownloadTemplate = () => {
    const csv = generateCSVTemplate();
    downloadFile(csv, 'company-import-template.csv');
    toast.success('Template downloaded');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="page-title">Bulk Import</h1>
        <p className="page-description">
          Import companies and contacts from a CSV file.
        </p>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-center gap-4 py-4">
        {(['upload', 'mapping', 'preview', 'complete'] as const).map((s, i) => (
          <React.Fragment key={s}>
            <div
              className={`flex items-center gap-2 ${
                step === s
                  ? 'text-blue-600 font-medium'
                  : step === 'complete' ||
                    (step === 'importing' && s !== 'complete') ||
                    (['mapping', 'preview', 'importing', 'complete'].indexOf(step) >
                      ['upload', 'mapping', 'preview', 'complete'].indexOf(s))
                  ? 'text-green-600'
                  : 'text-gray-400'
              }`}
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step === s
                    ? 'bg-blue-100 text-blue-600'
                    : ['mapping', 'preview', 'importing', 'complete'].indexOf(step) >
                      ['upload', 'mapping', 'preview', 'complete'].indexOf(s)
                    ? 'bg-green-100 text-green-600'
                    : 'bg-gray-100 text-gray-400'
                }`}
              >
                {['mapping', 'preview', 'importing', 'complete'].indexOf(step) >
                ['upload', 'mapping', 'preview', 'complete'].indexOf(s) ? (
                  <CheckCircle size={16} />
                ) : (
                  i + 1
                )}
              </div>
              <span className="hidden sm:inline capitalize">{s}</span>
            </div>
            {i < 3 && <ArrowRight size={16} className="text-gray-300" />}
          </React.Fragment>
        ))}
      </div>

      {/* Step: Upload */}
      {step === 'upload' && (
        <div className="card">
          <div className="max-w-xl mx-auto text-center py-8">
            <FileSpreadsheet size={64} className="mx-auto text-gray-300 mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Upload CSV File</h2>
            <p className="text-gray-600 mb-6">
              Upload a CSV file containing company and contact information to import.
            </p>

            <div className="space-y-4">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="btn btn-primary w-full max-w-xs"
              >
                <Upload size={18} />
                Choose CSV File
              </button>

              <div className="text-gray-500">or</div>

              <button
                onClick={handleDownloadTemplate}
                className="btn btn-secondary w-full max-w-xs"
              >
                <Download size={18} />
                Download Template
              </button>
            </div>

            <div className="mt-8 p-4 bg-blue-50 rounded-lg text-left">
              <h3 className="font-medium text-blue-900 mb-2">Tips for successful import:</h3>
              <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
                <li>First row must contain column headers</li>
                <li>Company Name is the only required field</li>
                <li>Use consistent formatting for phone numbers</li>
                <li>Duplicate companies (by name) will be skipped by default</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Step: Mapping */}
      {step === 'mapping' && (
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Map Columns</h2>
          <p className="text-gray-600 mb-6">
            Match your CSV columns to the appropriate fields. We've auto-detected some mappings.
          </p>

          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            {mappings.map((mapping) => (
              <div
                key={mapping.sourceColumn}
                className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex-1">
                  <span className="font-medium text-gray-900">{mapping.sourceColumn}</span>
                  <span className="text-sm text-gray-500 ml-2">
                    (e.g., "{csvRows[0]?.[mapping.sourceColumn] || ''}")
                  </span>
                </div>
                <ArrowRight size={16} className="text-gray-400" />
                <select
                  value={mapping.targetField}
                  onChange={(e) => updateMapping(mapping.sourceColumn, e.target.value)}
                  className="input w-64"
                >
                  {targetFields.map((field) => (
                    <option key={field.value} value={field.value}>
                      {field.label}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>

          {!hasRequiredMapping && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle size={18} />
              <span>Company Name mapping is required</span>
            </div>
          )}

          <div className="mt-6 flex gap-3">
            <button onClick={handleReset} className="btn btn-secondary">
              Start Over
            </button>
            <button
              onClick={handleProceedToPreview}
              disabled={!hasRequiredMapping || isProcessing}
              className="btn btn-primary flex-1"
            >
              {isProcessing ? (
                <>
                  <RefreshCw className="animate-spin" size={18} />
                  Validating...
                </>
              ) : (
                <>
                  <ArrowRight size={18} />
                  Continue to Preview
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Step: Preview */}
      {step === 'preview' && validationResult && (
        <div className="space-y-6">
          {/* Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="card">
              <p className="text-sm text-gray-600">Total Rows</p>
              <p className="text-2xl font-semibold">{csvRows.length}</p>
            </div>
            <div className="card">
              <p className="text-sm text-green-600">Valid</p>
              <p className="text-2xl font-semibold text-green-600">
                {validationResult.validRows}
              </p>
            </div>
            <div className="card">
              <p className="text-sm text-red-600">Invalid</p>
              <p className="text-2xl font-semibold text-red-600">
                {validationResult.invalidRows}
              </p>
            </div>
            <div className="card">
              <p className="text-sm text-orange-600">Duplicates</p>
              <p className="text-2xl font-semibold text-orange-600">
                {validationResult.duplicates.length}
              </p>
            </div>
          </div>

          {/* Errors */}
          {validationResult.errors.length > 0 && (
            <div className="card border-red-200">
              <h3 className="font-medium text-red-800 mb-3 flex items-center gap-2">
                <AlertCircle size={18} />
                Errors ({validationResult.errors.length} rows)
              </h3>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {validationResult.errors.map((e) => (
                  <div key={e.row} className="text-sm text-red-700 bg-red-50 p-2 rounded">
                    <strong>Row {e.row}:</strong> {e.errors.join(', ')}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Warnings */}
          {validationResult.warnings.length > 0 && (
            <div className="card border-yellow-200">
              <h3 className="font-medium text-yellow-800 mb-3 flex items-center gap-2">
                <AlertTriangle size={18} />
                Warnings ({validationResult.warnings.length} rows)
              </h3>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {validationResult.warnings.map((w) => (
                  <div key={w.row} className="text-sm text-yellow-700 bg-yellow-50 p-2 rounded">
                    <strong>Row {w.row}:</strong> {w.warnings.join(', ')}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Duplicates */}
          {validationResult.duplicates.length > 0 && (
            <div className="card border-orange-200">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium text-orange-800 flex items-center gap-2">
                  <AlertTriangle size={18} />
                  Duplicates ({validationResult.duplicates.length} companies)
                </h3>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={skipDuplicates}
                    onChange={(e) => setSkipDuplicates(e.target.checked)}
                    className="rounded"
                  />
                  Skip duplicates
                </label>
              </div>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {validationResult.duplicates.map((d) => (
                  <div key={d.row} className="text-sm text-orange-700 bg-orange-50 p-2 rounded">
                    <strong>Row {d.row}:</strong> "{d.existingCompanyName}" already exists
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            <button onClick={() => setStep('mapping')} className="btn btn-secondary">
              Back to Mapping
            </button>
            <button onClick={handleReset} className="btn btn-secondary">
              Start Over
            </button>
            <button
              onClick={handleExecuteImport}
              disabled={validationResult.validRows === 0}
              className="btn btn-primary flex-1"
            >
              <CheckCircle size={18} />
              Import {validationResult.validRows} Companies
            </button>
          </div>
        </div>
      )}

      {/* Step: Importing */}
      {step === 'importing' && (
        <div className="card">
          <div className="text-center py-12">
            <RefreshCw size={48} className="mx-auto text-blue-600 animate-spin mb-4" />
            <h2 className="text-xl font-semibold text-gray-900">Importing...</h2>
            <p className="text-gray-600 mt-2">Please wait while we import your data.</p>
          </div>
        </div>
      )}

      {/* Step: Complete */}
      {step === 'complete' && importResult && (
        <div className="card">
          <div className="text-center py-8">
            <CheckCircle size={64} className="mx-auto text-green-500 mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Import Complete!</h2>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto mt-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600">Total Rows</p>
                <p className="text-2xl font-semibold">{importResult.totalRows}</p>
              </div>
              <div className="bg-green-50 rounded-lg p-4">
                <p className="text-sm text-green-600">Companies</p>
                <p className="text-2xl font-semibold text-green-600">
                  {importResult.importedCompanies}
                </p>
              </div>
              <div className="bg-blue-50 rounded-lg p-4">
                <p className="text-sm text-blue-600">Contacts</p>
                <p className="text-2xl font-semibold text-blue-600">
                  {importResult.importedContacts}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600">Skipped</p>
                <p className="text-2xl font-semibold">{importResult.skippedRows}</p>
              </div>
            </div>

            {importResult.errors.length > 0 && (
              <div className="mt-6 text-left max-w-2xl mx-auto">
                <h3 className="font-medium text-red-800 mb-2">
                  {importResult.errors.length} rows had errors:
                </h3>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {importResult.errors.map((e) => (
                    <div key={e.row} className="text-sm text-red-700 bg-red-50 p-2 rounded">
                      Row {e.row}: {e.error}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-8 flex gap-3 justify-center">
              <button onClick={handleReset} className="btn btn-secondary">
                Import More
              </button>
              <a href="/companies" className="btn btn-primary">
                View Companies
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
